//CORE
import React from "react";
import ReactExport from "react-data-export";
import moment from "moment-timezone";
import MD5 from "crypto-js/md5";

//REDUX
import reduxStore from "store/";

//@MATERIAL
import CustomButton from "components/Buttons/custom"
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import Icon from "@material-ui/core/Icon";

//FUNCTIONS
import { translate, getDate } from "functions/";

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

// Estilos para o componente
const useStyles = makeStyles(theme => ({
    exportButton: {
        margin: theme.spacing(1),
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    exportIcon: {
        marginRight: theme.spacing(1),
    },
    noDataMessage: {
        padding: theme.spacing(2),
        color: theme.palette.text.secondary,
        fontStyle: 'italic'
    }
}));

function ExportAll(props) {
    const classes = useStyles();
    const [dataCompleted, setDataCompleted] = React.useState(false);
    const [overviewData, setOverviewData] = React.useState({
        guidelines: [],
        objectives: [],
        goals: [],
        actions: []
    });
    const [userDataSet, setUserDataSet] = React.useState([]);

    // Estado para nomes customizados das colunas
    const [customColumnNames, setCustomColumnNames] = React.useState({
        guidelines: translate('$__guidelines', 1),
        objectives: translate('$__objectives', 1),
        goals: translate('$__goals', 1),
        actions: translate('$__actions', 1)
    });

    const { db } = reduxStore.getState();

    // Cria um mapa de tipos de cards para tradução
    const cardTypeMap = {
        "guideline": translate('$__guideline', 1),
        "guidelineR": translate('$__guideline', 1) + " R",
        "guidelineD": translate('$__guideline', 1) + " D",
        "guidelineC": translate('$__guideline', 1) + " C",
        "guidelineE": translate('$__guideline', 1) + " E",
        "objective": translate('$__objective', 1),
        "goal": translate('$__goal', 1),
        "action": translate('$__action', 1),
        "task": translate('$__task', 1),
        "initiative": translate('$__initiative', 1)
    };

    // Função para criar mapa de relacionamento entre cards
    const createCardRelationMap = (cards) => {
        const map = {};

        // Primeiro passamos por todos os cards para construir o mapa
        Object.keys(cards).forEach(cardId => {
            try {
                const card = cards[cardId];
                if (!card) return;

                const parentId = card._parent;

                if (!map[cardId]) {
                    map[cardId] = {
                        id: cardId,
                        parentId: parentId,
                        type: card.type,
                        name: card.name || '',
                        code: card._cardCode || cardId.substring(0, 8),
                        ancestors: []
                    };
                }
            } catch (err) {
                console.error(`Erro ao mapear card ${cardId}:`, err);
            }
        });

        // Agora preenchemos os ancestrais para cada card
        Object.keys(map).forEach(cardId => {
            try {
                fillAncestors(cardId, map);
            } catch (err) {
                console.error(`Erro ao preencher ancestrais do card ${cardId}:`, err);
            }
        });

        return map;
    };

    // Função auxiliar para preencher ancestrais recursivamente
    const fillAncestors = (cardId, map, depth = 0) => {
        // Limite de profundidade para evitar loops infinitos
        if (depth > 10) return [];

        const card = map[cardId];
        if (!card) return [];

        // Se já calculamos os ancestrais, apenas retornamos
        if (card.ancestors.length > 0) return card.ancestors;

        // Se não tem pai, não tem ancestrais
        if (!card.parentId || !map[card.parentId]) return [];

        // Adiciona o pai como primeiro ancestral
        const parent = map[card.parentId];
        card.ancestors = [parent];

        // Adiciona os ancestrais do pai
        const parentAncestors = fillAncestors(card.parentId, map, depth + 1);
        if (parentAncestors && parentAncestors.length > 0) {
            card.ancestors = [...card.ancestors, ...parentAncestors];
        }

        return card.ancestors;
    };

    // Função auxiliar para processar um card para exportação
    const processCardForExport = (card, type, relationMap, allCards) => {
        if (!card) return null;

        try {
            // Obter o card do mapa de relações
            const cardRelation = relationMap[card._id];
            const ancestors = cardRelation ? cardRelation.ancestors : [];

            // Encontrar ancestrais por tipo
            const guideline = ancestors.find(a => a.type && a.type.indexOf("guideline") > -1);
            const objective = ancestors.find(a => a.type === "objective");
            const goal = ancestors.find(a => a.type === "goal");

            // Obter responsável
            let responsibleName = "";
            if (card._users) {
                const responsible = Object.values(card._users).find(user => user.isResponsible);
                if (responsible) {
                    responsibleName = responsible.name;
                } else {
                    const admin = Object.values(card._users).find(user => String(user.access).startsWith("5"));
                    if (admin) responsibleName = admin.name;
                }
            }

            // Processar tags
            let tagsStr = "";
            if (card._tags && Array.isArray(card._tags)) {
                tagsStr = card._tags
                    .filter(tag => tag && tag.label)
                    .map(tag => tag.label)
                    .join(" || ");
            }

            // Processar datas
            const startDate = card.startDate ?
                getDate(card.startDate.low || card.startDate, "YYYY-MM-DD HH:mm") : "";
            const endDate = card.endDate ?
                getDate(card.endDate.low || card.endDate, "YYYY-MM-DD HH:mm") : "";
            const updated_at = card.updated_at ?
                getDate(card.updated_at.low || card.updated_at, "YYYY-MM-DD HH:mm") : "";

            // Resultado base
            const result = {
                name: card.name || "",
                description: card.description || "",
                status: card.status || "",
                startDate,
                endDate,
                priority: card.priority || "",
                createdBy: card._createdBy?.name || "",
                responsible: responsibleName,
                updated_at,
                parent: card._parent && allCards[card._parent] ? allCards[card._parent].name : "",
                cardCode: card._cardCode || card._id.substring(0, 8),
                tags: tagsStr
            };

            // Adicionar informações de hierarquia
            if (guideline) {
                result.parentGuidelineName = guideline.name;
                result.parentGuidelineCode = guideline.code;
            }

            if (objective) {
                result.parentObjectiveName = objective.name;
                result.parentObjectiveCode = objective.code;
            }

            if (goal) {
                result.parentGoalName = goal.name;
                result.parentGoalCode = goal.code;
            }

            // Para objetivos, adicionar apenas guideline como pai
            if (type === "objective") {
                result.parentGuidelineName = guideline ? guideline.name : "";
                result.parentGuidelineCode = guideline ? guideline.code : "";
            }

            // Para metas, adicionar objective e guideline como ancestrais
            if (type === "goal") {
                result.parentObjectiveName = objective ? objective.name : "";
                result.parentObjectiveCode = objective ? objective.code : "";
                result.grandparentGuidelineName = guideline ? guideline.name : "";
                result.grandparentGuidelineCode = guideline ? guideline.code : "";
            }

            // Para ações, adicionar goal, objective e guideline como ancestrais
            if (type === "action") {
                result.parentGoalName = goal ? goal.name : "";
                result.parentGoalCode = goal ? goal.code : "";
                result.grandparentObjectiveName = objective ? objective.name : "";
                result.grandparentObjectiveCode = objective ? objective.code : "";
                result.greatGrandparentGuidelineName = guideline ? guideline.name : "";
                result.greatGrandparentGuidelineCode = guideline ? guideline.code : "";
            }

            // Adicionar campos específicos por tipo
            if (type === "guideline") {
                result.ref1 = card.ref1 || "";
                result.ref2 = card.ref2 || "";
                result.risk = card.risk || "";
                result.impact = card.impact || "";
            } else if (type === "objective") {
                result.ref2 = card.ref2 || "";
                result.complexity = card.complexity || "";
            } else if (type === "goal") {
                result.value = formatValue(card.value);
                result.progress = card.progress || "";
            }

            return result;
        } catch (error) {
            console.error(`Erro ao processar card ${card._id} para exportação:`, error);
            return null;
        }
    };

    // Função para formatar valores
    const formatValue = (value) => {
        if (!value) return "";

        if (typeof value === 'object') {
            if (value.low !== undefined) {
                return String(value.low);
            } else if (Array.isArray(value)) {
                return value.join(" | ");
            } else {
                return JSON.stringify(value);
            }
        }
        return String(value);
    };

    // Função para obter nível de acesso
    const getAccessLevel = (access) => {
        const levels = {
            "0": translate('$__reader', 1),
            "1": translate('$__executioner', 1),
            "2": translate('$__collaborator', 1),
            "3": translate('$__collaborator', 1),
            "4": translate('$__supervisor', 1),
            "5": translate('$__admin', 1)
        };

        if (!access) return translate('$__unknown', 1);

        return levels[String(access).substr(0, 1)] || access;
    };

    // Função de processamento de dados da view do Overview
    const processOverviewData = () => {
        console.log("Processando dados do Overview via props.data");

        // Obter nomes customizados das colunas, se disponíveis
        if (props.data && props.data.customColumnNames) {
            setCustomColumnNames(props.data.customColumnNames);
        }

        // Verificar se temos dados para processar
        if (props.data && props.data.activeCards) {
            const activeCards = props.data.activeCards;

            // Verificar se temos cards ativos
            if (Object.keys(activeCards).length > 0) {
                try {
                    // Processar os cards ativos de forma similar ao processCardsFromProps
                    const allCards = { ...activeCards };
                    const relationMap = createCardRelationMap(allCards);

                    // Classificar cards por tipo
                    const result = {
                        guidelines: [],
                        objectives: [],
                        goals: [],
                        actions: []
                    };

                    Object.values(activeCards).forEach(card => {
                        if (!card || !card._id) return;

                        try {
                            if (card.type && card.type.indexOf("guideline") > -1) {
                                const processedCard = processCardForExport(card, "guideline", relationMap, allCards);
                                if (processedCard) {
                                    result.guidelines.push(processedCard);
                                }
                            } else if (card.type === "objective" && !card.checklist) {
                                const processedCard = processCardForExport(card, "objective", relationMap, allCards);
                                if (processedCard) {
                                    result.objectives.push(processedCard);
                                }
                            } else if (card.type === "goal") {
                                const processedCard = processCardForExport(card, "goal", relationMap, allCards);
                                if (processedCard) {
                                    result.goals.push(processedCard);
                                }
                            } else if (
                                card.type &&
                                !card.checklist &&
                                card.type !== "plan" &&
                                card.type !== "objective" &&
                                card.type !== "goal" &&
                                card.type !== "feed" &&
                                card.type !== "link" &&
                                card.type !== "insight" &&
                                card.type !== "chatGroup" &&
                                card.type.indexOf("guideline") === -1 &&
                                db.dataType.filter(dt => dt.name === card.type).length > 0 &&
                                db.dataType.filter(dt => dt.name === card.type)[0].module !== "swot"
                            ) {
                                const processedCard = processCardForExport(card, "action", relationMap, allCards);
                                if (processedCard) {
                                    result.actions.push(processedCard);
                                }
                            }
                        } catch (err) {
                            console.error(`Erro ao processar card ${card._id}:`, err);
                        }
                    });

                    // Processar dados de usuários
                    const usersData = [];

                    Object.values(allCards).forEach(card => {
                        if (card && card._users) {
                            Object.keys(card._users).forEach(userId => {
                                try {
                                    const user = card._users[userId];
                                    if (user) {
                                        usersData.push({
                                            cardCode: card._cardCode || card._id.substring(0, 8),
                                            cardName: card.name || "",
                                            cardType: cardTypeMap[card.type] || card.type || "",
                                            user: user.name || userId,
                                            responsible: user.isResponsible ? "Sim" : "Não",
                                            level: getAccessLevel(user.access)
                                        });
                                    }
                                } catch (err) {
                                    console.error(`Erro ao processar usuário ${userId}:`, err);
                                }
                            });
                        }
                    });

                    setOverviewData(result);
                    setUserDataSet(usersData);
                    setDataCompleted(true);
                } catch (error) {
                    console.error("Erro ao processar dados do Overview:", error);
                    setDataCompleted(true);
                }
            } else {
                console.log("Nenhum card ativo encontrado em props.data");
                setDataCompleted(true);
            }
        } else {
            console.log("props.data não contém dados de cards ativos");
            setDataCompleted(true);
        }
    };

    // Função principal para processar dados
    const processCardsFromProps = React.useCallback(() => {
        try {
            console.log("Iniciando processamento de cards das props");

            // Obter nomes customizados das colunas, se disponíveis
            if (props.customColumnNames) {
                setCustomColumnNames(props.customColumnNames);
            }

            // Verificar se os dados foram passados via props.cards
            if (props.cards && typeof props.cards === 'object') {
                const columnNames = Object.keys(props.cards);

                if (columnNames.length === 0) {
                    console.log("Nenhuma coluna encontrada em props.cards");
                    setDataCompleted(true);
                    return;
                }

                console.log(`Processando ${columnNames.length} colunas de cards`);

                // Construir um objeto para todos os cards para facilitar referências
                const allCards = {};

                // Primeiro passe: coletar todos os cards para criar o mapa de relações
                columnNames.forEach(columnName => {
                    const columnCards = props.cards[columnName] || [];
                    columnCards.forEach(card => {
                        if (card && card._id) {
                            allCards[card._id] = card;
                        }
                    });
                });

                console.log(`Total de ${Object.keys(allCards).length} cards coletados`);

                // Criar mapa de relações entre cards
                const relationMap = createCardRelationMap(allCards);

                // Processar cada coluna para as respectivas planilhas
                const result = {
                    guidelines: [],
                    objectives: [],
                    goals: [],
                    actions: []
                };

                columnNames.forEach(columnName => {
                    const columnCards = props.cards[columnName] || [];

                    columnCards.forEach(card => {
                        if (!card || !card._id) return;

                        try {
                            // Determinar o tipo de card e processar adequadamente
                            if (card.type && card.type.indexOf("guideline") > -1) {
                                const processedCard = processCardForExport(card, "guideline", relationMap, allCards);
                                if (processedCard) {
                                    result.guidelines.push(processedCard);
                                }
                            } else if (card.type === "objective" && !card.checklist) {
                                const processedCard = processCardForExport(card, "objective", relationMap, allCards);
                                if (processedCard) {
                                    result.objectives.push(processedCard);
                                }
                            } else if (card.type === "goal") {
                                const processedCard = processCardForExport(card, "goal", relationMap, allCards);
                                if (processedCard) {
                                    result.goals.push(processedCard);
                                }
                            } else if (
                                card.type &&
                                !card.checklist &&
                                card.type !== "plan" &&
                                card.type !== "objective" &&
                                card.type !== "goal" &&
                                card.type !== "feed" &&
                                card.type !== "link" &&
                                card.type !== "insight" &&
                                card.type !== "chatGroup" &&
                                card.type.indexOf("guideline") === -1 &&
                                db.dataType.filter(dt => dt.name === card.type).length > 0 &&
                                db.dataType.filter(dt => dt.name === card.type)[0].module !== "swot"
                            ) {
                                const processedCard = processCardForExport(card, "action", relationMap, allCards);
                                if (processedCard) {
                                    result.actions.push(processedCard);
                                }
                            }
                        } catch (err) {
                            console.error(`Erro ao processar card ${card._id}:`, err);
                        }
                    });
                });

                console.log("Processamento concluído:", {
                    guidelines: result.guidelines.length,
                    objectives: result.objectives.length,
                    goals: result.goals.length,
                    actions: result.actions.length
                });

                // Processar dados de usuários
                const usersData = [];

                Object.values(allCards).forEach(card => {
                    if (card && card._users) {
                        Object.keys(card._users).forEach(userId => {
                            try {
                                const user = card._users[userId];
                                if (user) {
                                    usersData.push({
                                        cardCode: card._cardCode || card._id.substring(0, 8),
                                        cardName: card.name || "",
                                        cardType: cardTypeMap[card.type] || card.type || "",
                                        user: user.name || userId,
                                        responsible: user.isResponsible ? "Sim" : "Não",
                                        level: getAccessLevel(user.access)
                                    });
                                }
                            } catch (err) {
                                console.error(`Erro ao processar usuário ${userId}:`, err);
                            }
                        });
                    }
                });

                // Atualizar estados com os dados processados
                setOverviewData(result);
                setUserDataSet(usersData);
                setDataCompleted(true);
            } else if (props.data) {
                // Manter o código existente para processOverviewData para compatibilidade
                processOverviewData();
            } else {
                console.log("Nenhum dado encontrado para processar");
                setDataCompleted(true);
            }
        } catch (error) {
            console.error("Erro geral no processamento de cards:", error);
            setDataCompleted(true);
        }
    }, [props.cards, props.customColumnNames, props.data]);

    // Efeito para processar os dados quando as props mudarem
    React.useEffect(() => {
        processCardsFromProps();
    }, [processCardsFromProps]);

    // Renderização condicional com base no estado dos dados
    if (!dataCompleted) {
        return (
            <Tooltip title={translate('$__processingData', 1)}>
                <div className={classes.noDataMessage}>
                    <Icon>hourglass_empty</Icon> {translate('$__processingData', 1)}
                </div>
            </Tooltip>
        );
    }

    if (dataCompleted && Object.values(overviewData).every(arr => arr.length === 0)) {
        return (
            <Tooltip title={translate('$__noDataToExport', 1)}>
                <div className={classes.noDataMessage}>
                    <Icon>info</Icon> {translate('$__noDataToExport', 1)}
                </div>
            </Tooltip>
        );
    }

    // Renderização do componente ExcelFile com os dados processados
    return (
        <ExcelFile
            filename={`${props.fileName ? props.fileName.replace(" ", "-") : "exportOverview"}-${moment().unix('x')}`}
            element={
                <CustomButton
                    title={translate("$__export")}
                    text={translate("$__export")}
                    icon={"file_download"}
                    size={"25px"}
                    className={classes.exportButton}
                />
            }
        >
            {/* Planilha de Diretrizes */}
            {overviewData.guidelines.length > 0 && (
                <ExcelSheet
                    data={overviewData.guidelines}
                    name={customColumnNames.guidelines || translate('$__guidelines', 1)}
                >
                    <ExcelColumn label={translate('$__name', 1)} value="name" />
                    <ExcelColumn label={translate('$__description', 1)} value="description" />
                    <ExcelColumn label={translate('$__cardCode', 1)} value="cardCode" />
                    <ExcelColumn label={translate('$__status', 1)} value="status" />
                    <ExcelColumn label={translate('$__startDate', 1)} value="startDate" />
                    <ExcelColumn label={translate('$__endDate', 1)} value="endDate" />
                    <ExcelColumn label={translate('$__priority', 1)} value="priority" />
                    <ExcelColumn label={translate('$__ref1', 1)} value="ref1" />
                    <ExcelColumn label={translate('$__ref2', 1)} value="ref2" />
                    <ExcelColumn label={translate('$__risk', 1)} value="risk" />
                    <ExcelColumn label={translate('$__impact', 1)} value="impact" />
                    <ExcelColumn label={translate('$__createdBy', 1)} value="createdBy" />
                    <ExcelColumn label={translate('$__responsible', 1)} value="responsible" />
                    <ExcelColumn label={translate('$__updatedAt', 1)} value="updated_at" />
                </ExcelSheet>
            )}

            {/* Planilha de Objetivos */}
            {overviewData.objectives.length > 0 && (
                <ExcelSheet
                    data={overviewData.objectives}
                    name={customColumnNames.objectives || translate('$__objectives', 1)}
                >
                    <ExcelColumn label={translate('$__name', 1)} value="name" />
                    <ExcelColumn label={translate('$__description', 1)} value="description" />
                    <ExcelColumn label={translate('$__cardCode', 1)} value="cardCode" />
                    <ExcelColumn label={translate('$__status', 1)} value="status" />
                    <ExcelColumn label={translate('$__parentGuideline', 1)} value="parentGuidelineName" />
                    <ExcelColumn label={translate('$__parentCode', 1)} value="parentGuidelineCode" />
                    <ExcelColumn label={translate('$__startDate', 1)} value="startDate" />
                    <ExcelColumn label={translate('$__endDate', 1)} value="endDate" />
                    <ExcelColumn label={translate('$__priority', 1)} value="priority" />
                    <ExcelColumn label={translate('$__ref2', 1)} value="ref2" />
                    <ExcelColumn label={translate('$__complexity', 1)} value="complexity" />
                    <ExcelColumn label={translate('$__createdBy', 1)} value="createdBy" />
                    <ExcelColumn label={translate('$__responsible', 1)} value="responsible" />
                    <ExcelColumn label={translate('$__updatedAt', 1)} value="updated_at" />
                </ExcelSheet>
            )}

            {/* Planilha de Metas */}
            {overviewData.goals.length > 0 && (
                <ExcelSheet
                    data={overviewData.goals}
                    name={customColumnNames.goals || translate('$__goals', 1)}
                >
                    <ExcelColumn label={translate('$__name', 1)} value="name" />
                    <ExcelColumn label={translate('$__description', 1)} value="description" />
                    <ExcelColumn label={translate('$__cardCode', 1)} value="cardCode" />
                    <ExcelColumn label={translate('$__status', 1)} value="status" />
                    <ExcelColumn label={translate('$__parentObjective', 1)} value="parentObjectiveName" />
                    <ExcelColumn label={translate('$__parentCode', 1)} value="parentObjectiveCode" />
                    <ExcelColumn label={translate('$__grandparentGuideline', 1)} value="grandparentGuidelineName" />
                    <ExcelColumn label={translate('$__grandparentCode', 1)} value="grandparentGuidelineCode" />
                    <ExcelColumn label={translate('$__startDate', 1)} value="startDate" />
                    <ExcelColumn label={translate('$__endDate', 1)} value="endDate" />
                    <ExcelColumn label={translate('$__priority', 1)} value="priority" />
                    <ExcelColumn label={translate('$__value', 1)} value="value" />
                    <ExcelColumn label={translate('$__progress', 1)} value="progress" />
                    <ExcelColumn label={translate('$__createdBy', 1)} value="createdBy" />
                    <ExcelColumn label={translate('$__responsible', 1)} value="responsible" />
                    <ExcelColumn label={translate('$__updatedAt', 1)} value="updated_at" />
                </ExcelSheet>
            )}

            {/* Planilha de Ações */}
            {overviewData.actions.length > 0 && (
                <ExcelSheet
                    data={overviewData.actions}
                    name={customColumnNames.actions || translate('$__actions', 1)}
                >
                    <ExcelColumn label={translate('$__name', 1)} value="name" />
                    <ExcelColumn label={translate('$__description', 1)} value="description" />
                    <ExcelColumn label={translate('$__cardCode', 1)} value="cardCode" />
                    <ExcelColumn label={translate('$__status', 1)} value="status" />
                    <ExcelColumn label={translate('$__parentGoal', 1)} value="parentGoalName" />
                    <ExcelColumn label={translate('$__parentCode', 1)} value="parentGoalCode" />
                    <ExcelColumn label={translate('$__grandparentObjective', 1)} value="grandparentObjectiveName" />
                    <ExcelColumn label={translate('$__grandparentCode', 1)} value="grandparentObjectiveCode" />
                    <ExcelColumn label={translate('$__greatGrandparentGuideline', 1)} value="greatGrandparentGuidelineName" />
                    <ExcelColumn label={translate('$__greatGrandparentCode', 1)} value="greatGrandparentGuidelineCode" />
                    <ExcelColumn label={translate('$__startDate', 1)} value="startDate" />
                    <ExcelColumn label={translate('$__endDate', 1)} value="endDate" />
                    <ExcelColumn label={translate('$__priority', 1)} value="priority" />
                    <ExcelColumn label={translate('$__tags', 1)} value="tags" />
                    <ExcelColumn label={translate('$__createdBy', 1)} value="createdBy" />
                    <ExcelColumn label={translate('$__responsible', 1)} value="responsible" />
                    <ExcelColumn label={translate('$__updatedAt', 1)} value="updated_at" />
                </ExcelSheet>
            )}

            {/* Planilha de Usuários */}
            {userDataSet.length > 0 && (
                <ExcelSheet
                    data={userDataSet}
                    name={translate('$__users', 1)}
                >
                    <ExcelColumn label={translate('$__cardCode', 1)} value="cardCode" />
                    <ExcelColumn label={translate('$__cardName', 1)} value="cardName" />
                    <ExcelColumn label={translate('$__cardType', 1)} value="cardType" />
                    <ExcelColumn label={translate('$__user', 1)} value="user" />
                    <ExcelColumn label={translate('$__access', 1)} value="level" />
                    <ExcelColumn label={translate('$__isResposible', 1)} value="responsible" />
                </ExcelSheet>
            )}
        </ExcelFile>
    );
}

export default ExportAll;